import { useEffect, useRef, useState, useCallback } from 'react';
import io from 'socket.io-client';
import { msalInstance, apiTokenRequest } from '../services/authConfig';
import { config } from '../config/config';

type Socket = ReturnType<typeof io>;

export interface ActiveUser {
  sid: string;
  user_name: string;
  user_id: string;
  user_email?: string;
  timestamp?: string;
}

export interface LiveUpdate {
  update_type: string;
  payload: any;
  user: ActiveUser;
  timestamp: string;
}

interface UseRealtimeCollaborationOptions {
  pageId: string;
  userName?: string;
  userId?: string;
  userEmail?: string;
  onLiveUpdate?: (update: LiveUpdate) => void;
  onUserJoined?: (data: { user: ActiveUser; active_users: ActiveUser[] }) => void;
  onUserLeft?: (data: { user: ActiveUser; active_users: ActiveUser[] }) => void;
  enabled?: boolean;
}

interface UseRealtimeCollaborationReturn {
  socket: Socket | null;
  activeUsers: ActiveUser[];
  broadcastUpdate: (updateType: string, payload: any) => void;
  isConnected: boolean;
  connectionError: string | null;
}

/**
 * React hook for real-time collaboration using Socket.IO
 *
 * @example
 * ```tsx
 * const { activeUsers, broadcastUpdate, isConnected } = useRealtimeCollaboration({
 *   pageId: 'dispatch-planner',
 *   userName: currentUser.name,
 *   userId: currentUser.id,
 *   userEmail: currentUser.email,
 *   onLiveUpdate: (update) => {
 *     console.log('Received update:', update);
 *     // Handle the update (e.g., refresh data, update UI)
 *   },
 *   onUserJoined: ({ user, active_users }) => {
 *     console.log(`${user.user_name} joined. Total users: ${active_users.length}`);
 *   },
 *   onUserLeft: ({ user, active_users }) => {
 *     console.log(`${user.user_name} left. Total users: ${active_users.length}`);
 *   },
 * });
 *
 * // Broadcast an update when data changes
 * const handleDataChange = (newData) => {
 *   broadcastUpdate('data_updated', { data: newData });
 * };
 * ```
 */
export function useRealtimeCollaboration({
  pageId,
  userName = 'Anonymous',
  userId,
  userEmail,
  onLiveUpdate,
  onUserJoined,
  onUserLeft,
  enabled = true,
}: UseRealtimeCollaborationOptions): UseRealtimeCollaborationReturn {
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Store callbacks in refs so the socket effect doesn't re-run when they change.
  // This prevents socket reconnection (and stale-closure data fetches) on every filter change.
  const onLiveUpdateRef = useRef(onLiveUpdate);
  const onUserJoinedRef = useRef(onUserJoined);
  const onUserLeftRef = useRef(onUserLeft);
  useEffect(() => { onLiveUpdateRef.current = onLiveUpdate; }, [onLiveUpdate]);
  useEffect(() => { onUserJoinedRef.current = onUserJoined; }, [onUserJoined]);
  useEffect(() => { onUserLeftRef.current = onUserLeft; }, [onUserLeft]);

  // Broadcast an update to all users in the room
  const broadcastUpdate = useCallback(
    (updateType: string, payload: any) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('broadcast_update', {
          page_id: pageId,
          update_type: updateType,
          payload,
          timestamp: new Date().toISOString(),
        });
      } else {
        console.warn('Socket not connected, cannot broadcast update');
      }
    },
    [pageId, isConnected]
  );

  useEffect(() => {
    // Check if real-time collaboration is disabled via environment variable
    const realtimeEnabled = import.meta.env.VITE_REALTIME_ENABLED !== 'false';

    if (!enabled || !realtimeEnabled) {
      return;
    }

    let socket: Socket | null = null;
    let cancelled = false;

    // Get MSAL token for Socket.IO authentication
    const initializeSocket = async () => {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts || accounts.length === 0) {
          console.warn('[Socket.IO] No authenticated account found');
          return;
        }

        const request = {
          ...apiTokenRequest,
          account: accounts[0],
        };
        const response = await msalInstance.acquireTokenSilent(request);

        if (!response.accessToken || cancelled) {
          if (!response.accessToken) console.warn('[Socket.IO] No access token available');
          return;
        }

        // Create Socket.IO client connection with authentication
        const socketUrl = config.API_URL.replace('/api/v1', ''); // Remove /api/v1 suffix for socket connection

        socket = io(socketUrl, {
          path: '/ws',
          auth: {
            token: `Bearer ${response.accessToken}`
          },
          // Use WebSocket transport directly to avoid multi-worker session issues with polling
          // HTTP polling doesn't work well with load-balanced servers unless sticky sessions
          // are perfectly configured. WebSocket maintains a persistent connection to one worker.
          transports: ['websocket'],
          reconnection: true,
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          reconnectionAttempts: 3, // Reduced from 5 to avoid spam
          timeout: 10000, // 10 second connection timeout
        });

        socketRef.current = socket;

        // Connection event handlers
        socket.on('connect', () => {
          setIsConnected(true);
          setConnectionError(null);

          // Join the page room
          socket!.emit('join_page', {
            page_id: pageId,
            user_name: userName,
            user_id: userId,
            user_email: userEmail,
            timestamp: new Date().toISOString(),
          });
        });

        socket.on('disconnect', () => {
          setIsConnected(false);
          setActiveUsers([]);
        });

        socket.on('connect_error', (error: Error) => {
          setConnectionError(error.message);
          setIsConnected(false);
        });

        // Handle failed reconnection attempts
        socket.io.on('reconnect_failed', () => {
          setConnectionError('Failed to establish real-time connection');
          socket?.disconnect();
        });

        // Page collaboration event handlers — use refs so handlers always see latest callbacks
        socket.on('user_joined', (data: { user: ActiveUser; active_users: ActiveUser[] }) => {
          setActiveUsers(data.active_users);
          onUserJoinedRef.current?.(data);
        });

        socket.on('user_left', (data: { user: ActiveUser; active_users: ActiveUser[] }) => {
          setActiveUsers(data.active_users);
          onUserLeftRef.current?.(data);
        });

        socket.on('active_users_list', (data: { active_users: ActiveUser[] }) => {
          setActiveUsers(data.active_users);
        });

        socket.on('live_update', (update: LiveUpdate) => {
          onLiveUpdateRef.current?.(update);
        });
      } catch (error) {
        console.error('[Socket.IO] Failed to initialize:', error);
        setConnectionError('Failed to initialize real-time connection');
      }
    };

    initializeSocket();

    // Cleanup: disconnect socket when effect re-runs or component unmounts
    return () => {
      cancelled = true;
      if (socket) {
        if (socket.connected) {
          socket.emit('leave_page', { page_id: pageId });
        }
        socket.disconnect();
      }
      socketRef.current = null;
    };
  }, [enabled, pageId, userName, userId, userEmail]);

  return {
    socket: socketRef.current,
    activeUsers,
    broadcastUpdate,
    isConnected,
    connectionError,
  };
}
